import { type HighlighterGeneric, createHighlighter } from 'shiki';

/**
 * Singleton shiki highlighter. The highlighter loads with NO
 * grammars — each language is pulled in on first use via
 * `loadLanguage`. Pre-loading every grammar we might need cost
 * several MB of JS at initial page load; most chats only touch
 * 2-3 languages ever.
 */

// Curated allowlist — CodeBlock normalises fenced-block info
// strings through this; unknown languages fall back to plain text
// so we don't pull arbitrary grammars at the user's request.
const LANGS = [
  'bash',
  'sh',
  'javascript',
  'typescript',
  'jsx',
  'tsx',
  'python',
  'go',
  'rust',
  'json',
  'yaml',
  'html',
  'css',
  'markdown',
  'sql',
  'diff',
  'dockerfile',
];

type Hl = HighlighterGeneric<string, string>;

let instance: Promise<Hl> | null = null;
const loadedLangs = new Set<string>();

export function getHighlighter(): Promise<Hl> {
  if (!instance) {
    instance = createHighlighter({
      themes: ['github-light', 'github-dark'],
      langs: [],
    }) as Promise<Hl>;
  }
  return instance;
}

/**
 * Ensure a given shiki grammar is loaded before highlighting. Safe
 * to call repeatedly — the Set short-circuits subsequent requests
 * and shiki itself dedupes concurrent loads of the same lang.
 */
export async function ensureLanguage(lang: string): Promise<void> {
  if (lang === 'text' || loadedLangs.has(lang)) return;
  const hl = await getHighlighter();
  try {
    // `loadLanguage` accepts a bundled-language name directly.
    await hl.loadLanguage(lang as Parameters<typeof hl.loadLanguage>[0]);
    loadedLangs.add(lang);
  } catch {
    // Unknown language — swallow so CodeBlock can fall back to plain.
  }
}

/**
 * Normalize a markdown code fence's info string ("```ts", "```sh#n")
 * to a language shiki will accept, or null for "treat as plain".
 */
export function normalizeLang(raw: string | null | undefined): string {
  if (!raw) return 'text';
  const first = raw.split(/\s/)[0]?.toLowerCase() ?? '';
  if (!first) return 'text';
  if (LANGS.includes(first)) return first;
  return 'text';
}
